import {
  canonicalDirectionToResolvedSide,
  planarTopologyOutwardDocFaceDirection,
} from "building-generation/buildingGraph/resolveFaceSide";
import type { BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { WallPlaneSubdomain } from "building-types/walls2/wallPlane";
import type { PlanarTopologyAssemblySlot } from "building-types/walls2/wallPlaneOperations";
import { defaultWallPlaneEmptyAssemblyId } from "doc/defaults/defaultWallPlaneAssembly";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import { topologyRoomBackedPlaneUVAABB } from "./planarTopologySelectionGeometry";
import type { PlanarTopologySplitRegion } from "./planarTopologySplitRegions";
import { planarTopologyWallPlaneSubdomains } from "./planarTopologyWallCoreResolution";

export interface PlanarTopologyAssemblyRegion {
  readonly topologyId: string;
  readonly assemblyId: string;
  readonly uMin: number;
  readonly uMax: number;
  readonly vMin: number;
  readonly vMax: number;
}

function subdomainsShareEdge(a: WallPlaneSubdomain, b: WallPlaneSubdomain): boolean {
  const shareVerticalEdge =
    (Math.abs(a.maxX - b.minX) <= DIST_EPSILON || Math.abs(b.maxX - a.minX) <= DIST_EPSILON) &&
    Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY) > DIST_EPSILON;
  const shareHorizontalEdge =
    (Math.abs(a.maxY - b.minY) <= DIST_EPSILON || Math.abs(b.maxY - a.minY) <= DIST_EPSILON) &&
    Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX) > DIST_EPSILON;
  return shareVerticalEdge || shareHorizontalEdge;
}

export function assemblyKeyForSubdomain(
  building: Pick<Building, "buildingGraph" | "wallPlanes">,
  topology: BuildingGraphPlanarTopology,
  subdomain: WallPlaneSubdomain,
  slot: PlanarTopologyAssemblySlot,
): string | undefined {
  const wallInfo = subdomain.wallInfo;
  if (!wallInfo) {
    return undefined;
  }

  if (slot === "wallCoreOperator") {
    return wallInfo.coreAssembly?.id ?? defaultWallPlaneEmptyAssemblyId;
  }

  const outwardDirection = planarTopologyOutwardDocFaceDirection(building.buildingGraph, topology);
  if (!outwardDirection) {
    return undefined;
  }

  const exteriorSide = canonicalDirectionToResolvedSide(outwardDirection);
  if (!exteriorSide) {
    return undefined;
  }

  const assembly = exteriorSide === "RIGHT" ? wallInfo.rightCladdingAssembly : wallInfo.leftCladdingAssembly;
  return assembly?.id ?? defaultWallPlaneEmptyAssemblyId;
}

export function groupContiguousSubdomainsByAssemblyKey(
  building: Pick<Building, "buildingGraph" | "wallPlanes">,
  topology: BuildingGraphPlanarTopology,
  subdomains: readonly WallPlaneSubdomain[],
  slot: PlanarTopologyAssemblySlot,
): readonly (readonly WallPlaneSubdomain[])[] {
  const keyedSubdomains: WallPlaneSubdomain[] = [];
  for (const subdomain of subdomains) {
    const key = assemblyKeyForSubdomain(building, topology, subdomain, slot);
    if (key !== undefined) {
      keyedSubdomains.push(subdomain);
    }
  }

  const components: WallPlaneSubdomain[][] = [];
  const visited = new Set<number>();

  for (let i = 0; i < keyedSubdomains.length; i++) {
    if (visited.has(i)) {
      continue;
    }

    const startSubdomain = keyedSubdomains[i];
    if (!startSubdomain) {
      continue;
    }

    const startKey = assemblyKeyForSubdomain(building, topology, startSubdomain, slot);
    const component: WallPlaneSubdomain[] = [];
    const queue: number[] = [i];
    visited.add(i);

    while (queue.length > 0) {
      const currentIndex = queue.pop();
      if (currentIndex === undefined) {
        continue;
      }
      const current = keyedSubdomains[currentIndex];
      if (!current) {
        continue;
      }
      component.push(current);

      for (let j = 0; j < keyedSubdomains.length; j++) {
        if (visited.has(j)) {
          continue;
        }
        const candidate = keyedSubdomains[j];
        if (!candidate) {
          continue;
        }
        const candidateKey = assemblyKeyForSubdomain(building, topology, candidate, slot);
        if (candidateKey !== startKey) {
          continue;
        }
        if (subdomainsShareEdge(current, candidate)) {
          visited.add(j);
          queue.push(j);
        }
      }
    }

    if (component.length > 0) {
      components.push(component);
    }
  }

  return components;
}

function approxEqual(a: number, b: number): boolean {
  return Math.abs(a - b) <= DIST_EPSILON;
}

function sameVBand(a: WallPlaneSubdomain, b: WallPlaneSubdomain): boolean {
  return approxEqual(a.minY, b.minY) && approxEqual(a.maxY, b.maxY);
}

function sortSubdomainsByUV(a: WallPlaneSubdomain, b: WallPlaneSubdomain): number {
  if (!approxEqual(a.minX, b.minX)) {
    return a.minX - b.minX;
  }
  if (!approxEqual(a.minY, b.minY)) {
    return a.minY - b.minY;
  }
  if (!approxEqual(a.maxX, b.maxX)) {
    return a.maxX - b.maxX;
  }
  return a.maxY - b.maxY;
}

/** Contiguous unused subdomains in the seed's V band, grown in U until a gap. */
function growURunFromSeed(
  seed: WallPlaneSubdomain,
  unused: ReadonlySet<WallPlaneSubdomain>,
): readonly WallPlaneSubdomain[] {
  const sameRow = [...unused].filter((subdomain) => sameVBand(subdomain, seed)).sort(sortSubdomainsByUV);
  if (sameRow.length === 0) {
    return [];
  }

  const seedIndex = sameRow.indexOf(seed);
  if (seedIndex < 0) {
    return [seed];
  }

  let startIndex = seedIndex;
  while (startIndex > 0) {
    const left = sameRow[startIndex - 1];
    const current = sameRow[startIndex];
    if (!left || !current || !approxEqual(left.maxX, current.minX)) {
      break;
    }
    startIndex -= 1;
  }

  let endIndex = seedIndex;
  while (endIndex < sameRow.length - 1) {
    const current = sameRow[endIndex];
    const right = sameRow[endIndex + 1];
    if (!current || !right || !approxEqual(current.maxX, right.minX)) {
      break;
    }
    endIndex += 1;
  }

  return sameRow.slice(startIndex, endIndex + 1);
}

/**
 * Unused subdomains adjacent across `vEdge` that cover exactly `[uMin, uMax]` with no gaps
 * and matching U endpoints. Returns undefined when the U span of coverage changes.
 */
function collectExactUSpanBandAtVEdge(
  uMin: number,
  uMax: number,
  vEdge: number,
  direction: "up" | "down",
  unused: ReadonlySet<WallPlaneSubdomain>,
): readonly WallPlaneSubdomain[] | undefined {
  const candidates = [...unused]
    .filter((subdomain) => {
      const touchesEdge = direction === "up" ? approxEqual(subdomain.minY, vEdge) : approxEqual(subdomain.maxY, vEdge);
      if (!touchesEdge) {
        return false;
      }
      return subdomain.maxX > uMin + DIST_EPSILON && subdomain.minX < uMax - DIST_EPSILON;
    })
    .sort(sortSubdomainsByUV);

  if (candidates.length === 0) {
    return undefined;
  }

  const first = candidates[0];
  const last = candidates[candidates.length - 1];
  if (!first || !last) {
    return undefined;
  }

  // U span must match exactly — otherwise the same-assembly U coverage changed.
  if (!approxEqual(first.minX, uMin) || !approxEqual(last.maxX, uMax)) {
    return undefined;
  }

  const bandVMin = first.minY;
  const bandVMax = first.maxY;
  for (const subdomain of candidates) {
    if (!approxEqual(subdomain.minY, bandVMin) || !approxEqual(subdomain.maxY, bandVMax)) {
      return undefined;
    }
  }

  for (let i = 0; i < candidates.length - 1; i++) {
    const current = candidates[i];
    const next = candidates[i + 1];
    if (!current || !next || !approxEqual(current.maxX, next.minX)) {
      return undefined;
    }
  }

  return candidates;
}

/**
 * Decompose a contiguous same-assembly subdomain group into maximal axis-aligned
 * rectangles by growing U first, then V while the U span stays covered.
 */
export function decomposeSubdomainGroupToRectangularRegions(
  topologyId: string,
  group: readonly WallPlaneSubdomain[],
  assemblyId: string,
): readonly PlanarTopologyAssemblyRegion[] {
  if (group.length === 0) {
    return [];
  }

  const unused = new Set<WallPlaneSubdomain>(group);
  const regions: PlanarTopologyAssemblyRegion[] = [];

  while (unused.size > 0) {
    const seed = [...unused].sort(sortSubdomainsByUV)[0];
    if (!seed) {
      break;
    }

    const uRun = growURunFromSeed(seed, unused);
    if (uRun.length === 0) {
      unused.delete(seed);
      continue;
    }

    const uRunFirst = uRun[0];
    const uRunLast = uRun[uRun.length - 1];
    if (!uRunFirst || !uRunLast) {
      unused.delete(seed);
      continue;
    }

    const uMin = uRunFirst.minX;
    const uMax = uRunLast.maxX;
    let vMin = uRunFirst.minY;
    let vMax = uRunFirst.maxY;

    for (const subdomain of uRun) {
      unused.delete(subdomain);
    }

    // Grow upward in V while the full U span remains covered by unused group members.
    for (;;) {
      const nextBand = collectExactUSpanBandAtVEdge(uMin, uMax, vMax, "up", unused);
      if (!nextBand) {
        break;
      }
      const bandFirst = nextBand[0];
      if (!bandFirst) {
        break;
      }
      vMax = bandFirst.maxY;
      for (const subdomain of nextBand) {
        unused.delete(subdomain);
      }
    }

    // Grow downward in V while the full U span remains covered.
    for (;;) {
      const nextBand = collectExactUSpanBandAtVEdge(uMin, uMax, vMin, "down", unused);
      if (!nextBand) {
        break;
      }
      const bandFirst = nextBand[0];
      if (!bandFirst) {
        break;
      }
      vMin = bandFirst.minY;
      for (const subdomain of nextBand) {
        unused.delete(subdomain);
      }
    }

    if (uMax > uMin + DIST_EPSILON && vMax > vMin + DIST_EPSILON) {
      regions.push({
        topologyId,
        assemblyId,
        uMin,
        uMax,
        vMin,
        vMax,
      });
    }
  }

  return regions;
}

export function mergeSubdomainGroupToRegion(
  topologyId: string,
  group: readonly WallPlaneSubdomain[],
  assemblyId: string,
): PlanarTopologyAssemblyRegion | undefined {
  if (group.length === 0) {
    return undefined;
  }

  let uMin = Number.POSITIVE_INFINITY;
  let uMax = Number.NEGATIVE_INFINITY;
  let vMin = Number.POSITIVE_INFINITY;
  let vMax = Number.NEGATIVE_INFINITY;

  for (const subdomain of group) {
    uMin = Math.min(uMin, subdomain.minX);
    uMax = Math.max(uMax, subdomain.maxX);
    vMin = Math.min(vMin, subdomain.minY);
    vMax = Math.max(vMax, subdomain.maxY);
  }

  if (uMax <= uMin + DIST_EPSILON || vMax <= vMin + DIST_EPSILON) {
    return undefined;
  }

  return {
    topologyId,
    assemblyId,
    uMin,
    uMax,
    vMin,
    vMax,
  };
}

function uvAabbsOverlap(
  a: { readonly uMin: number; readonly uMax: number; readonly vMin: number; readonly vMax: number },
  b: { readonly uMin: number; readonly uMax: number; readonly vMin: number; readonly vMax: number },
): boolean {
  return (
    a.uMax > b.uMin + DIST_EPSILON &&
    a.uMin < b.uMax - DIST_EPSILON &&
    a.vMax > b.vMin + DIST_EPSILON &&
    a.vMin < b.vMax - DIST_EPSILON
  );
}

function intersectUvAabb(
  a: { readonly uMin: number; readonly uMax: number; readonly vMin: number; readonly vMax: number },
  b: { readonly uMin: number; readonly uMax: number; readonly vMin: number; readonly vMax: number },
): { readonly uMin: number; readonly uMax: number; readonly vMin: number; readonly vMax: number } | undefined {
  const uMin = Math.max(a.uMin, b.uMin);
  const uMax = Math.min(a.uMax, b.uMax);
  const vMin = Math.max(a.vMin, b.vMin);
  const vMax = Math.min(a.vMax, b.vMax);
  if (uMax <= uMin + DIST_EPSILON || vMax <= vMin + DIST_EPSILON) {
    return undefined;
  }
  return { uMin, uMax, vMin, vMax };
}

export function intersectAssemblyRegionsWithDatumSplits(
  assemblyRegions: readonly PlanarTopologyAssemblyRegion[],
  datumSplitRegions: readonly PlanarTopologySplitRegion[],
): readonly PlanarTopologyAssemblyRegion[] {
  if (datumSplitRegions.length === 0) {
    return assemblyRegions;
  }

  const hasDatumBounds = datumSplitRegions.some(
    (region) =>
      region.startHorizontalDatumId !== undefined ||
      region.endHorizontalDatumId !== undefined ||
      region.startVerticalDatumId !== undefined ||
      region.endVerticalDatumId !== undefined,
  );
  if (!hasDatumBounds) {
    return assemblyRegions;
  }

  const intersected: PlanarTopologyAssemblyRegion[] = [];
  for (const assemblyRegion of assemblyRegions) {
    for (const datumRegion of datumSplitRegions) {
      if (!uvAabbsOverlap(assemblyRegion, datumRegion)) {
        continue;
      }
      const overlap = intersectUvAabb(assemblyRegion, datumRegion);
      if (!overlap) {
        continue;
      }
      intersected.push({
        topologyId: assemblyRegion.topologyId,
        assemblyId: assemblyRegion.assemblyId,
        ...overlap,
      });
    }
  }

  return intersected;
}

export function computePlanarTopologyAssemblyRegions(
  building: Pick<Building, "buildingGraph" | "wallPlanes">,
  topologyId: string,
  slot: PlanarTopologyAssemblySlot,
): readonly PlanarTopologyAssemblyRegion[] {
  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return [];
  }

  // Always merge full-height assembly bands. Callers that need plan-level pick
  // filtering should use filterPlanarTopologyAssemblyRegionsTouchingPlanLevels.
  const subdomains = planarTopologyWallPlaneSubdomains(building, topologyId);

  const groups = groupContiguousSubdomainsByAssemblyKey(building, topology, subdomains, slot);

  const regions: PlanarTopologyAssemblyRegion[] = [];
  for (const group of groups) {
    const first = group[0];
    if (!first) {
      continue;
    }
    const assemblyId = assemblyKeyForSubdomain(building, topology, first, slot);
    if (assemblyId === undefined) {
      continue;
    }
    regions.push(...decomposeSubdomainGroupToRectangularRegions(topologyId, group, assemblyId));
  }

  return regions;
}

/**
 * Keep assembly regions whose UV overlaps the plan-level room-backed AABB, without
 * clipping region UV. Double-height bands that span multiple levels stay intact and
 * remain the same pick target on each level's plan view; stacked per-level bands that
 * only live on another story are dropped.
 */
export function filterPlanarTopologyAssemblyRegionsTouchingPlanLevels(
  building: Pick<Building, "buildingGraph">,
  topologyId: string,
  regions: readonly PlanarTopologyAssemblyRegion[],
  planLevelIds: ReadonlySet<string>,
): readonly PlanarTopologyAssemblyRegion[] {
  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return [];
  }

  const planLevelAabb = topologyRoomBackedPlaneUVAABB(topology, building.buildingGraph, planLevelIds);
  if (!planLevelAabb) {
    return [];
  }

  return regions.filter((region) =>
    uvAabbsOverlap(region, {
      uMin: planLevelAabb.minX,
      uMax: planLevelAabb.maxX,
      vMin: planLevelAabb.minY,
      vMax: planLevelAabb.maxY,
    }),
  );
}

export function assemblyRegionToSplitRegion(
  assemblyRegion: PlanarTopologyAssemblyRegion,
  datumRegion?: PlanarTopologySplitRegion,
): PlanarTopologySplitRegion {
  return {
    uMin: assemblyRegion.uMin,
    uMax: assemblyRegion.uMax,
    vMin: assemblyRegion.vMin,
    vMax: assemblyRegion.vMax,
    ...(datumRegion?.startHorizontalDatumId !== undefined
      ? { startHorizontalDatumId: datumRegion.startHorizontalDatumId }
      : {}),
    ...(datumRegion?.endHorizontalDatumId !== undefined
      ? { endHorizontalDatumId: datumRegion.endHorizontalDatumId }
      : {}),
    ...(datumRegion?.startVerticalDatumId !== undefined
      ? { startVerticalDatumId: datumRegion.startVerticalDatumId }
      : {}),
    ...(datumRegion?.endVerticalDatumId !== undefined ? { endVerticalDatumId: datumRegion.endVerticalDatumId } : {}),
  };
}

/** Plan view: keep only the topmost assembly region per overlapping U span. */
export function applyPlanViewAssemblyRegionOcclusion(
  regions: readonly PlanarTopologyAssemblyRegion[],
): readonly PlanarTopologyAssemblyRegion[] {
  if (regions.length <= 1) {
    return regions;
  }

  const uBreakpoints = new Set<number>();
  for (const region of regions) {
    uBreakpoints.add(region.uMin);
    uBreakpoints.add(region.uMax);
  }
  const sortedU = [...uBreakpoints].sort((a, b) => a - b);

  const occluded: PlanarTopologyAssemblyRegion[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < sortedU.length - 1; i++) {
    const uLo = sortedU[i];
    const uHi = sortedU[i + 1];
    if (uLo === undefined || uHi === undefined || uHi <= uLo + DIST_EPSILON) {
      continue;
    }

    let bestRegion: PlanarTopologyAssemblyRegion | undefined;
    for (const region of regions) {
      if (region.uMax <= uLo + DIST_EPSILON || region.uMin >= uHi - DIST_EPSILON) {
        continue;
      }
      if (!bestRegion || region.vMax > bestRegion.vMax + DIST_EPSILON) {
        bestRegion = region;
      }
    }

    if (!bestRegion) {
      continue;
    }

    const key = `${bestRegion.topologyId}:${bestRegion.assemblyId}:${bestRegion.uMin}:${bestRegion.uMax}:${bestRegion.vMin}:${bestRegion.vMax}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    occluded.push(bestRegion);
  }

  return occluded.length > 0 ? occluded : regions;
}

export function subdomainAabbFromRegion(region: PlanarTopologyAssemblyRegion): AABB2 {
  return {
    minX: region.uMin,
    minY: region.vMin,
    maxX: region.uMax,
    maxY: region.vMax,
  };
}
